// src/components/maintenance/utils/csvParser.js
import { supabaseClient } from '../../supabase';
import { parseCSV } from './csvGenerator';

/**
 * Fetch all records from a table using pagination
 */
async function fetchAllRecords(table, selectQuery) {
  const pageSize = 1000;
  let allRecords = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    const { data, error } = await supabaseClient
      .from(table)
      .select(selectQuery)
      .range(from, to);

    if (error) throw error;

    if (data && data.length > 0) {
      allRecords = allRecords.concat(data);
      page++;

      if (data.length < pageSize) {
        hasMore = false;
      }
    } else {
      hasMore = false;
    }
  }

  console.log(`Fetched ${allRecords.length} records from ${table}`);
  return allRecords;
}

/**
 * Normalize empty values (null, undefined, empty string) to empty string for comparison
 */
function normalizeEmpty(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return String(value).trim();
}

/**
 * Compare two arrays for equality
 */
function arraysEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) return false;
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }
  return true;
}

/**
 * Parse all uploaded CSVs and generate comprehensive preview
 */
export async function parseAndGeneratePreviewMultiple(uploadedFiles, maintenanceRecord) {
  try {
    // Download and parse all three CSVs
    const [teachersData, classesData, studentsParentsData] = await Promise.all([
      downloadAndParseCSV(uploadedFiles.teachers.path),
      downloadAndParseCSV(uploadedFiles.classes.path),
      downloadAndParseCSV(uploadedFiles.students_parents.path),
    ]);

    // Fetch current database state
    const [currentTeachers, currentClasses, currentStudents, currentParents] = await Promise.all([
      fetchCurrentTeachers(),
      fetchCurrentClasses(),
      fetchCurrentStudents(),
      fetchCurrentParents(),
    ]);

    // Generate previews for each entity type
    const teachersPreview = generateTeachersPreview(teachersData, currentTeachers);
    const classesPreview = generateClassesPreview(classesData, currentClasses, teachersData);
    const studentsPreview = await generateStudentsPreview(
      studentsParentsData,
      currentStudents,
      currentParents,
      classesData,
      maintenanceRecord
    );

    return {
      teachers: teachersPreview,
      classes: classesPreview,
      students: studentsPreview.students,
      parents: studentsPreview.parents,
      householdConflicts: studentsPreview.householdConflicts,
      totalRows: {
        teachers: teachersData.length,
        classes: classesData.length,
        students: studentsParentsData.length,
      },
    };
  } catch (error) {
    console.error('Error parsing CSVs:', error);
    throw error;
  }
}

/**
 * Download and parse a CSV from storage
 */
async function downloadAndParseCSV(filePath) {
  const { data, error } = await supabaseClient.storage
    .from('maintenance-files')
    .download(filePath);

  if (error) throw error;

  const text = await data.text();
  return parseCSV(text);
}

/**
 * Fetch current teachers from database
 */
async function fetchCurrentTeachers() {
  return await fetchAllRecords(
    'teachers',
    `
      id,
      first_name,
      last_name,
      email,
      mobile,
      role,
      class_id,
      classes (
        class_code
      )
    `
  );
}

/**
 * Fetch current classes from database
 */
async function fetchCurrentClasses() {
  return await fetchAllRecords('classes', '*');
}

/**
 * Fetch current students from database
 */
async function fetchCurrentStudents() {
  return await fetchAllRecords(
    'students',
    `
      id,
      first_name,
      last_name,
      household_hash,
      grade,
      class_id,
      classes (
        class_code,
        grade
      )
    `
  );
}

/**
 * Fetch current parents from database
 */
async function fetchCurrentParents() {
  return await fetchAllRecords('parents', '*');
}

/**
 * Generate teachers preview (new, updated, removed)
 */
function generateTeachersPreview(csvTeachers, currentTeachers) {
  const newTeachers = [];
  const updatedTeachers = [];
  const csvTeacherIds = new Set();

  const currentTeachersMap = new Map(currentTeachers.map(t => [t.id, t]));
  const currentTeachersByEmail = new Map(
    currentTeachers.filter(t => t.email).map(t => [t.email.toLowerCase().trim(), t])
  );

  csvTeachers.forEach(csvTeacher => {
    const teacherId = csvTeacher['Teacher ID'] ? parseInt(csvTeacher['Teacher ID']) : null;
    const email = normalizeEmpty(csvTeacher['Email']).toLowerCase();
    const firstName = normalizeEmpty(csvTeacher['First Name']);
    const lastName = normalizeEmpty(csvTeacher['Last Name']);
    const mobile = normalizeEmpty(csvTeacher['Mobile']);
    const role = normalizeEmpty(csvTeacher['Role']) || 'teacher';  // ← NEW
    const classCode = normalizeEmpty(csvTeacher['Class Code (Owner)']);

    if (teacherId && currentTeachersMap.has(teacherId)) {
      // Existing teacher
      csvTeacherIds.add(teacherId);
      const current = currentTeachersMap.get(teacherId);
      const changes = [];

      // Normalize all values before comparison
      if (normalizeEmpty(current.first_name) !== firstName) {
        changes.push({ field: 'first_name', old: current.first_name, new: firstName });
      }
      if (normalizeEmpty(current.last_name) !== lastName) {
        changes.push({ field: 'last_name', old: current.last_name, new: lastName });
      }
      if (normalizeEmpty(current.email).toLowerCase() !== email) {
        changes.push({ field: 'email', old: current.email, new: email });
      }
      if (normalizeEmpty(current.mobile) !== mobile) {
        changes.push({ field: 'mobile', old: current.mobile, new: mobile });
      }    
      if (normalizeEmpty(current.role) !== role) {  // ← NEW
        changes.push({ field: 'role', old: current.role, new: role });
      }

      // Compare class codes (handle null/empty)
      const currentClassCode = normalizeEmpty(current.classes?.class_code);
      if (currentClassCode !== classCode) {
        changes.push({ 
          field: 'class_code', 
          old: currentClassCode || '(none)', 
          new: classCode || '(none)' 
        });
      }

      if (changes.length > 0) {
        updatedTeachers.push({
          id: teacherId,
          first_name: firstName,
          last_name: lastName,
          email: email,
          mobile: mobile,
          role: role,  // ← NEW
          class_code: classCode,
          changes,
        });
      }
    } else if (email && currentTeachersByEmail.has(email)) {
      // Teacher exists but ID is missing/wrong - match by email
      const current = currentTeachersByEmail.get(email);
      csvTeacherIds.add(current.id);

      const changes = [];
      if (normalizeEmpty(current.first_name) !== firstName) {
        changes.push({ field: 'first_name', old: current.first_name, new: firstName });
      }
      if (normalizeEmpty(current.last_name) !== lastName) {
        changes.push({ field: 'last_name', old: current.last_name, new: lastName });
      }
      if (normalizeEmpty(current.mobile) !== mobile) {
        changes.push({ field: 'mobile', old: current.mobile, new: mobile });
      }
      if (normalizeEmpty(current.role) !== role) {  // ← NEW
        changes.push({ field: 'role', old: current.role, new: role });
      }

      const currentClassCode = normalizeEmpty(current.classes?.class_code);
      if (currentClassCode !== classCode) {
        changes.push({ 
          field: 'class_code', 
          old: currentClassCode || '(none)', 
          new: classCode || '(none)' 
        });
      }

      if (changes.length > 0) {
        updatedTeachers.push({
          id: current.id,
          first_name: firstName,
          last_name: lastName,
          email: email,
          mobile: mobile,
          role: role,  // ← NEW
          class_code: classCode,
          changes,
        });
      }
    } else {
      // New teacher
      newTeachers.push({
        first_name: firstName,
        last_name: lastName,
        email: email,
        mobile: mobile,
        role: role,  // ← NEW
        class_code: classCode,
      });
    }
  });

  // Find removed teachers (in DB but not in CSV)
  const removedTeachers = currentTeachers
    .filter(teacher => !csvTeacherIds.has(teacher.id))
    .map(teacher => ({
      id: teacher.id,
      first_name: teacher.first_name,
      last_name: teacher.last_name,
      email: teacher.email,
      role: teacher.role,  // ← NEW (for display)
    }));

  return {
    new: newTeachers,
    updated: updatedTeachers,
    removed: removedTeachers,
  };
}

/**
 * Generate classes preview (new, updated, removed)
 */
function generateClassesPreview(csvClasses, currentClasses, csvTeachers) {
  const newClasses = [];
  const updatedClasses = [];
  const csvClassIds = new Set();

  const currentClassesMap = new Map(currentClasses.map(c => [c.id, c]));
  const currentClassesByCode = new Map(currentClasses.map(c => [c.class_code, c]));

  // Build map of class codes to teacher emails from CSV
  const classCodeToTeacher = new Map();
  csvTeachers.forEach(teacher => {
    const classCode = normalizeEmpty(teacher['Class Code (Owner)']);
    if (classCode) {
      classCodeToTeacher.set(classCode, normalizeEmpty(teacher['Email']));
    }
  });

  csvClasses.forEach(csvClass => {
    const classId = csvClass['Class ID'] ? parseInt(csvClass['Class ID']) : null;
    const classCode = normalizeEmpty(csvClass['Class Code']);
    const grade = csvClass['Grade']?.trim();
    const gradeNum = grade !== '' ? parseInt(grade) : null;

    if (classId && currentClassesMap.has(classId)) {
      // Existing class
      csvClassIds.add(classId);
      const current = currentClassesMap.get(classId);
      const changes = [];

      if (normalizeEmpty(current.class_code) !== classCode) {
        changes.push({ field: 'class_code', old: current.class_code, new: classCode });
      }
      if (current.grade !== gradeNum) {
        changes.push({ field: 'grade', old: current.grade, new: gradeNum });
      }

      if (changes.length > 0) {
        updatedClasses.push({
          id: classId,
          class_code: classCode,
          grade: gradeNum,
          teacher_email: classCodeToTeacher.get(classCode),
          changes,
        });
      }
    } else if (classCode && currentClassesByCode.has(classCode)) {
      // Class exists but ID is missing/wrong - match by class_code
      const current = currentClassesByCode.get(classCode);
      csvClassIds.add(current.id);

      const changes = [];
      if (current.grade !== gradeNum) {
        changes.push({ field: 'grade', old: current.grade, new: gradeNum });
      }

      if (changes.length > 0) {
        updatedClasses.push({
          id: current.id,
          class_code: classCode,
          grade: gradeNum,
          teacher_email: classCodeToTeacher.get(classCode),
          changes,
        });
      }
    } else {
      // New class
      newClasses.push({
        class_code: classCode,
        grade: gradeNum,
        teacher_email: classCodeToTeacher.get(classCode),
      });
    }
  });

  // Find removed classes (in DB but not in CSV)
  const removedClasses = currentClasses
    .filter(cls => !csvClassIds.has(cls.id))
    .map(cls => ({
      id: cls.id,
      class_code: cls.class_code,
      grade: cls.grade,
    }));

  return {
    new: newClasses,
    updated: updatedClasses,
    removed: removedClasses,
  };
}

/**
 * Generate students/parents preview
 */
async function generateStudentsPreview(
  csvStudents,
  currentStudents,
  currentParents,
  csvClasses,
  maintenanceRecord
) {
  const newStudents = [];
  const updatedStudents = [];
  const newParents = [];
  const updatedParents = [];
  const householdConflicts = [];
  const studentsWithoutParents = [];  // Track students missing parent data (grades 0-6 only)
  const studentsToDelete = [];  // Track students not in CSV (non-grade-7)
  const csvStudentIds = new Set();

  // Build class code to class data map from CSV classes
  const classCodeMap = new Map();
  csvClasses.forEach(csvClass => {
    const classCode = normalizeEmpty(csvClass['Class Code']);
    const classId = csvClass['Class ID'] ? parseInt(csvClass['Class ID']) : null;
    const grade = csvClass['Grade']?.trim();
    const gradeNum = grade !== '' ? parseInt(grade) : null;

    if (classCode) {
      classCodeMap.set(classCode, { id: classId, grade: gradeNum });
    }
  });

  const studentMap = new Map(currentStudents.map(s => [s.id, s]));
  const parentsByEmail = new Map();
  const parentsByHousehold = new Map();

  currentParents.forEach(parent => {
    if (parent.email) {
      const emailKey = normalizeEmpty(parent.email).toLowerCase();
      parentsByEmail.set(emailKey, parent);
    }
    if (!parentsByHousehold.has(parent.household_hash)) {
      parentsByHousehold.set(parent.household_hash, []);
    }
    parentsByHousehold.get(parent.household_hash).push(parent);
  });

  // Process CSV rows
  for (const row of csvStudents) {
    const studentId = row['Student ID'] ? parseInt(row['Student ID']) : null;
    const firstName = normalizeEmpty(row['First Name']);
    const lastName = normalizeEmpty(row['Last Name']);
    const classCode = normalizeEmpty(row['Class Code']);

    if (!firstName || !lastName || !classCode) {
      console.warn('Skipping invalid row:', row);
      continue;
    }

    const classData = classCodeMap.get(classCode);
    if (!classData) {
      throw new Error(`Invalid class code: ${classCode} - not found in classes.csv`);
    }

    // Extract parent data
    const parents = extractParentsFromRow(row);

    if (parents.length === 0) {
      // Track the student ID so they're not considered "missing from CSV"
      if (studentId) {
        csvStudentIds.add(studentId);
        
        // Only report missing parent data for grades 0-6 (not alumni)
        if (classData.grade >= 0 && classData.grade <= 6) {
          studentsWithoutParents.push({
            id: studentId,
            first_name: firstName,
            last_name: lastName,
            class_code: classCode,
            grade: classData.grade,
          });
        }
      }
      console.warn('Student without parents in CSV:', firstName, lastName, studentId, `grade ${classData.grade}`);
      continue;
    }

    // Determine household
    const householdInfo = determineHouseholdImproved(
      parents,
      parentsByEmail,
      parentsByHousehold,
      studentId,
      studentMap
    );

    if (studentId && studentMap.has(studentId)) {
      // Existing student - check for updates
      csvStudentIds.add(studentId);
      const currentStudent = studentMap.get(studentId);
      const changes = [];

      // Only add changes if values actually differ (normalized comparison)
      if (normalizeEmpty(currentStudent.first_name) !== firstName) {
        changes.push({ field: 'first_name', old: currentStudent.first_name, new: firstName });
      }
      if (normalizeEmpty(currentStudent.last_name) !== lastName) {
        changes.push({ field: 'last_name', old: currentStudent.last_name, new: lastName });
      }
      if (normalizeEmpty(currentStudent.classes?.class_code) !== classCode) {
        changes.push({
          field: 'class',
          old: currentStudent.classes?.class_code,
          new: classCode
        });
      }

      // Check for household change ONLY if parents actually changed
      const parentsChanged = checkIfParentsChanged(
        parents,
        parentsByHousehold.get(currentStudent.household_hash) || []
      );

      // Also check if household_hash actually differs
      const householdHashChanged = currentStudent.household_hash !== householdInfo.household_hash;

      if (parentsChanged && householdHashChanged) {
        // This is a real conflict - parent info changed
        householdConflicts.push({
          student: {
            id: studentId,
            first_name: firstName,
            last_name: lastName,
          },
          currentHousehold: currentStudent.household_hash,
          newHousehold: householdInfo.household_hash,
          currentParents: parentsByHousehold.get(currentStudent.household_hash) || [],
          newParents: parents,
          siblings: currentStudents.filter(s =>
            s.household_hash === currentStudent.household_hash && s.id !== studentId
          ),
        });
        
        // Add to updated with household change
        updatedStudents.push({
          id: studentId,
          first_name: firstName,
          last_name: lastName,
          class_code: classCode,
          class_id: classData.id,
          grade: classData.grade,
          household_hash: householdInfo.household_hash,
          changes: [...changes, { field: 'household', old: 'existing', new: 'changed' }],
          parents: householdInfo.parents,
          isNewHousehold: householdInfo.isNew,
        });
      } else if (changes.length > 0) {
        // Only student data changed (name, class), no household change
        updatedStudents.push({
          id: studentId,
          first_name: firstName,
          last_name: lastName,
          class_code: classCode,
          class_id: classData.id,
          grade: classData.grade,
          household_hash: householdInfo.household_hash,
          changes,
          parents: householdInfo.parents,
          isNewHousehold: householdInfo.isNew,
        });
      }
      // If no changes at all, don't add to updated list
    } else {
      // New student
      newStudents.push({
        first_name: firstName,
        last_name: lastName,
        class_code: classCode,
        class_id: classData.id,
        grade: classData.grade,
        household_hash: householdInfo.household_hash,
        parents: householdInfo.parents,
        isNewHousehold: householdInfo.isNew,
      });
    }
  }

  // Find graduating students (grade 7 not in CSV)
  const graduatingStudents = currentStudents.filter(student =>
    student.grade === 7 && !csvStudentIds.has(student.id)
  ).map(student => ({
    id: student.id,
    first_name: student.first_name,
    last_name: student.last_name,
    current_class: student.classes?.class_code,
    alumni_class: maintenanceRecord.alumni_class_name,
  }));

  // Find students to potentially delete (grades 0-6, not in CSV, not alumni)
  // These will be shown with a toggle to decide whether to delete or not
  const potentialDeletions = currentStudents.filter(student =>
    student.grade >= 0 && 
    student.grade <= 6 && 
    !csvStudentIds.has(student.id)
  ).map(student => ({
    id: student.id,
    first_name: student.first_name,
    last_name: student.last_name,
    grade: student.grade,
    current_class: student.classes?.class_code,
  }));

  console.log('Total grade 7 students in DB:', currentStudents.filter(s => s.grade === 7).length);
  console.log('Grade 7 students found in CSV:', Array.from(csvStudentIds).filter(id => {
    const student = studentMap.get(id);
    return student && student.grade === 7;
  }).length);
  console.log('Graduating students (grade 7 not in CSV):', graduatingStudents.length);
  console.log('Students without parent data (grades 0-6):', studentsWithoutParents.length);
  console.log('Students not in CSV (grades 0-6):', potentialDeletions.length);

  return {
    students: {
      new: newStudents,
      updated: updatedStudents,
      graduating: graduatingStudents,
    },
    parents: {
      new: newParents,
      updated: updatedParents,
    },
    householdConflicts,
    studentsWithoutParents,  // Grades 0-6 only
    studentsToDelete: potentialDeletions,  // Grades 0-6 not in CSV
  };
}

/**
 * Extract parent data from CSV row - handles dynamic number of parents
 */
function extractParentsFromRow(row) {
  const parents = [];
  const seenEmails = new Set();  // Track emails to avoid duplicates
  
  // Find all parent columns dynamically
  const headers = Object.keys(row);
  let parentIndex = 1;
  
  while (true) {
    const emailKey = `Parent ${parentIndex} Email`;
    
    // If this parent column doesn't exist, we're done
    if (!headers.includes(emailKey)) {
      break;
    }
    
    const email = normalizeEmpty(row[emailKey]).toLowerCase();
    
    // Only add parent if they have an email and we haven't seen this email yet
    if (email && !seenEmails.has(email)) {
      parents.push({
        first_name: normalizeEmpty(row[`Parent ${parentIndex} First Name`]),
        last_name: normalizeEmpty(row[`Parent ${parentIndex} Last Name`]),
        email: email,
        mobile: normalizeEmpty(row[`Parent ${parentIndex} Mobile`]),
      });
      seenEmails.add(email);
    }
    
    parentIndex++;
  }

  return parents;
}

/**
 * Improved household determination
 * Matches based on ALL parent emails in the household
 */
function determineHouseholdImproved(parents, parentsByEmail, parentsByHousehold, studentId, studentMap) {
  if (parents.length === 0) {
    throw new Error('At least one parent must be provided');
  }

  // Get parent emails from CSV (normalized)
  const csvParentEmails = parents
    .map(p => normalizeEmpty(p.email).toLowerCase())
    .filter(e => e)
    .sort();

  console.log('determineHouseholdImproved called:', {
    studentId,
    csvParentEmails,
    parentsInDb: csvParentEmails.map(email => ({
      email,
      exists: parentsByEmail.has(email)
    }))
  });

  // If this is an existing student, check their current household
  if (studentId && studentMap.has(studentId)) {
    const currentStudent = studentMap.get(studentId);
    const currentHouseholdParents = parentsByHousehold.get(currentStudent.household_hash) || [];
    const currentParentEmails = Array.from(new Set(
      currentHouseholdParents
        .map(p => normalizeEmpty(p.email).toLowerCase())
        .filter(e => e)
    )).sort();

    // If parent emails match exactly, use existing household
    if (arraysEqual(csvParentEmails, currentParentEmails)) {
      console.log('Using existing student household (emails match)');
      return {
        household_hash: currentStudent.household_hash,
        parents: currentHouseholdParents,
        isNew: false,
      };
    }
  }

  // Check if any parent exists in database by email
  const existingParents = parents
    .map(p => parentsByEmail.get(normalizeEmpty(p.email).toLowerCase()))
    .filter(p => p !== undefined);

  console.log('Existing parents found:', existingParents.length);

  if (existingParents.length > 0) {
    // Check if all CSV parent emails match the household's parent emails
    const household_hash = existingParents[0].household_hash;
    const householdParents = parentsByHousehold.get(household_hash) || [];
    
    const householdParentEmails = Array.from(new Set(
      householdParents
        .map(p => normalizeEmpty(p.email).toLowerCase())
        .filter(e => e)
    )).sort();

    console.log('Checking household match:', {
      csvEmails: csvParentEmails,
      householdEmails: householdParentEmails,
      matches: arraysEqual(csvParentEmails, householdParentEmails)
    });

    if (arraysEqual(csvParentEmails, householdParentEmails)) {
      // Perfect match - use existing household
      console.log('Using existing household (all emails match)');
      return {
        household_hash,
        parents: householdParents,
        isNew: false,
      };
    } else {
      console.log('Emails do NOT match - creating new household');
    }
  }

  // New household
  console.log('Creating brand new household');
  const household_hash = crypto.randomUUID();
  return {
    household_hash,
    parents: parents,
    isNew: true,
  };
}

/**
 * Check if parents actually changed (by comparing unique emails)
 */
function checkIfParentsChanged(newParents, currentParents) {
  // Get unique emails from both sets
  const newEmails = Array.from(new Set(
    newParents
      .map(p => normalizeEmpty(p.email).toLowerCase())
      .filter(e => e)
  )).sort();
  
  const currentEmails = Array.from(new Set(
    currentParents
      .map(p => normalizeEmpty(p.email).toLowerCase())
      .filter(e => e)
  )).sort();

  return !arraysEqual(newEmails, currentEmails);
}